<template>
  <div class="identifiers-container">
    <div class="header">
      <div class="title-section">
        <h2 class="title">{{ t('workspaceSettings.identifiers.title') }}</h2>
        <div class="description">
          {{ t('workspaceSettings.identifiers.description') }}
        </div>
      </div>
      <a-button type="primary" @click="handleCreate">
        <template #icon>
          <icon-plus />
        </template>
        {{ t('workspaceSettings.identifiers.create') }}
      </a-button>
    </div>

    <!-- 执行标识列表 -->
    <div class="protocol-list">
      <a-table
        :data="protocolList"
        :loading="loading"
        :pagination="pagination"
        @page-change="onPageChange"
      >
        <template #columns>
          <a-table-column
            :title="t('workspaceSettings.identifiers.column.identifier')"
            data-index="name"
          >
            <template #cell="{ record }">
              <div class="protocol-info">
                <div class="protocol-icon">
                  <icon-bookmark :size="32" />
                </div>
                <div class="info-content">
                  <div class="name">{{ record.name }}</div>
                  <div class="desc">{{ record.description || '-' }}</div>
                  <div class="meta">
                    {{
                      t('workspaceSettings.identifiers.targetModal.updatedAt')
                    }}
                    {{ formatDate(record.created_at) }}
                    {{ t('workspaceSettings.identifiers.targetModal.by') }}
                    {{ record.creator }}
                  </div>
                </div>
              </div>
            </template>
          </a-table-column>
          <a-table-column
            :title="t('workspaceSettings.identifiers.column.target')"
            data-index="target"
          >
            <template #cell="{ record }">
              <div class="target-info">
                <template v-if="record.target_workflow_uuid">
                  <div class="target-icon">
                    <img src="@/assets/images/workflow.png" alt="" />
                  </div>
                  <div class="info-content">
                    <div class="name">{{
                      record.target_workflow_name || '-'
                    }}</div>
                    <div class="desc">
                      {{ record.target_workflow_description || '-' }}
                    </div>
                    <a-tag
                      v-if="record.target_publish_version"
                      color="green"
                      size="small"
                    >
                      {{ record.target_publish_version }}
                    </a-tag>
                  </div>
                </template>
                <div v-else class="no-target">
                  <div class="target-icon">
                    <img src="@/assets/images/workflow.png" alt="" />
                  </div>
                  <span>{{
                    t('workspaceSettings.identifiers.targetModal.noTarget')
                  }}</span>
                </div>
              </div>
            </template>
          </a-table-column>
          <a-table-column
            :title="t('workspaceSettings.identifiers.column.lastUsed')"
            data-index="last_used_at"
          >
            <template #cell="{ record }">
              {{
                record.last_used_at
                  ? formatDate(record.last_used_at)
                  : t('workspaceSettings.identifiers.never')
              }}
            </template>
          </a-table-column>
          <a-table-column
            :title="t('workspaceSettings.identifiers.column.actions')"
            :width="120"
          >
            <template #cell="{ record }">
              <a-space>
                <a-tooltip
                  :content="t('workspaceSettings.identifiers.action.editMeta')"
                >
                  <a-button
                    type="text"
                    size="small"
                    @click="handleEditMeta(record)"
                  >
                    <template #icon><icon-edit /></template>
                  </a-button>
                </a-tooltip>
                <a-tooltip
                  :content="
                    t('workspaceSettings.identifiers.action.updateTarget')
                  "
                >
                  <a-button
                    type="text"
                    size="small"
                    @click="handleUpdateTarget(record)"
                  >
                    <template #icon><icon-settings /></template>
                  </a-button>
                </a-tooltip>
                <a-dropdown
                  trigger="click"
                  @select="(key) => handleMoreAction(key, record)"
                >
                  <a-button type="text" size="small">
                    <template #icon><icon-more /></template>
                  </a-button>
                  <template #content>
                    <a-doption value="history">
                      <template #icon><icon-history /></template>
                      {{
                        t('workspaceSettings.identifiers.action.updateHistory')
                      }}
                    </a-doption>
                    <a-doption value="delete" class="danger-option">
                      <template #icon><icon-delete /></template>
                      {{ t('workspaceSettings.identifiers.action.delete') }}
                    </a-doption>
                    <a-doption
                      value="open_target_workflow"
                      :disabled="!record.target_workflow_uuid"
                    >
                      <template #icon><icon-launch /></template>
                      {{
                        t(
                          'workspaceSettings.identifiers.action.open_target_workflow'
                        )
                      }}
                    </a-doption>
                  </template>
                </a-dropdown>
              </a-space>
            </template>
          </a-table-column>
        </template>
      </a-table>
    </div>

    <!-- 弹窗组件 -->
    <CreateProtocolModal v-model:visible="createVisible" @success="fetchList" />
    <EditProtocolMetaModal
      v-model:visible="editMetaVisible"
      :data="currentProtocol"
      @success="fetchList"
    />
    <UpdateProtocolTargetModal
      v-model:visible="updateTargetVisible"
      :data="currentProtocol"
      @success="fetchList"
    />
    <ProtocolHistoryModal v-model="historyVisible" :data="currentProtocol" />
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, reactive } from 'vue';
  import {
    IconPlus,
    IconBranch,
    IconEdit,
    IconMore,
  } from '@arco-design/web-vue/es/icon';
  import { Modal } from '@arco-design/web-vue';
  import Message from '@/utils/message';
  import dayjs from 'dayjs';
  import { useI18n } from 'vue-i18n';
  import { getProtocolListApi, deleteProtocolApi } from '@/api/protocol';
  import type { ProtocolItem } from '@/types/protocol';
  import { useUserStore } from '@/store';
  import { buildWorkspacePath } from '@/utils/route';
  import CreateProtocolModal from './identifiers/create-protocol-modal.vue';
  import EditProtocolMetaModal from './identifiers/edit-protocol-meta-modal.vue';
  import UpdateProtocolTargetModal from './identifiers/update-protocol-target-modal.vue';
  import ProtocolHistoryModal from './identifiers/protocol-history-modal.vue';

  const { t } = useI18n();
  const userStore = useUserStore();
  const loading = ref(false);
  const protocolList = ref<ProtocolItem[]>([]);
  const pagination = reactive({
    current: 1,
    pageSize: 50,
    total: 0,
    showTotal: true,
  });

  const createVisible = ref(false);
  const editMetaVisible = ref(false);
  const updateTargetVisible = ref(false);
  const historyVisible = ref(false);
  const currentProtocol = ref<ProtocolItem | null>(null);

  const formatDate = (date: string) => {
    if (!date) return '-';
    return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
  };

  const fetchList = async () => {
    loading.value = true;
    try {
      const res = await getProtocolListApi({
        tenant_uuid: userStore.tenantUuid!,
        workspace_uuid: userStore.workspaceUuid!,
        page_no: pagination.current,
        page_size: pagination.pageSize,
      });
      if (res.data?.results) {
        protocolList.value = res.data.results || [];
        pagination.total = res.data.total_count || 0;
      }
    } catch (error) {
      console.error(error);
    } finally {
      loading.value = false;
    }
  };

  const onPageChange = (page: number) => {
    pagination.current = page;
    fetchList();
  };

  const handleCreate = () => {
    // TODO: implementation
    createVisible.value = true;
  };

  const handleEditMeta = (record: ProtocolItem) => {
    currentProtocol.value = record;
    editMetaVisible.value = true;
  };

  const handleUpdateTarget = (record: ProtocolItem) => {
    currentProtocol.value = record;
    updateTargetVisible.value = true;
  };

  const handleMoreAction = (
    key: string | number | Record<string, any>,
    record: ProtocolItem
  ) => {
    if (key === 'history') {
      currentProtocol.value = record;
      historyVisible.value = true;
    } else if (key === 'delete') {
      const content = record.last_used_at
        ? t('workspaceSettings.identifiers.message.deleteConfirmContentUsed', {
            date: dayjs(record.last_used_at).format('MM/DD/YYYY HH:mm:ss'),
          })
        : t('workspaceSettings.identifiers.message.deleteConfirmContentUnused');

      Modal.confirm({
        title: t('workspaceSettings.identifiers.message.deleteConfirmTitle', {
          name: record.name,
        }),
        content,
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        width: 480,
        okButtonProps: { status: 'danger' },
        onOk: async () => {
          try {
            await deleteProtocolApi({
              tenant_uuid: userStore.tenantUuid!,
              workspace_uuid: userStore.workspaceUuid!,
              name: record.name,
            });
            Message.success(
              t('workspaceSettings.identifiers.message.deleteSuccess')
            );
            fetchList();
          } catch (error) {
            console.error(error);
          }
        },
      });
    } else if (key === 'open_target_workflow') {
      window.open(
        buildWorkspacePath(
          `/develop/workflow/detail?workflow_uuid=${record.target_workflow_uuid}`,
          userStore.workspaceUuid
        ),
        '_blank'
      );
    }
  };

  onMounted(() => {
    fetchList();
  });
</script>

<style lang="less" scoped>
  .identifiers-container {
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 24px;

      .title-section {
        .title {
          font-size: 20px;
          font-weight: 600;
          color: var(--color-text-1);
          margin-bottom: 8px;
        }

        .description {
          color: var(--color-text-3);
          font-size: 14px;
        }
      }
    }

    .protocol-list {
      flex: 1;
      overflow: hidden;
    }

    .protocol-info {
      display: flex;
      gap: 12px;

      .protocol-icon {
        width: 60px;
        height: 60px;
        border-radius: var(--border-radius-small);
        border: 1px solid var(--color-border-2);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--color-text-2);
      }

      .info-content {
        .name {
          font-weight: 500;
          color: var(--color-text-1);
          margin-bottom: 4px;
        }
        .desc {
          color: var(--color-text-3);
          font-size: 12px;
          margin-bottom: 4px;
        }
        .meta {
          color: var(--color-text-4);
          font-size: 12px;
        }
      }
    }

    .target-info {
      display: flex;
      gap: 12px;

      .target-icon {
        width: 60px;
        height: 60px;
        border-radius: var(--border-radius-small);
        border: 1px solid var(--color-border-2);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--color-text-2);
        img {
          width: 50%;
        }
      }

      .info-content {
        .name {
          font-weight: 500;
          color: var(--color-text-1);
          margin-bottom: 4px;
        }
        .desc {
          color: var(--color-text-3);
          font-size: 12px;
          margin-bottom: 4px;
        }
      }
    }

    .no-target {
      display: flex;
      gap: 8px;
      align-items: center;
      color: var(--color-text-3);
    }

    .danger-option {
      color: rgb(var(--red-6));
    }
  }
</style>
